import { Box, Button, Card, CardContent, Dialog, DialogContent, Typography } from "@mui/material";
import { useState } from "react";

import { GenericDescription } from "~ui/atoms/GenericDescription/GenericDescription";
import { CollapsibleDescription } from "~ui/molecules/CollapsibleDescription/CollapsibleDescription";

export interface PopOutDescriptionProps {
  /**
   * The description text to display
   */
  description?: string | null;
  /**
   * Optional title for the description section
   * @default "Description"
   */
  title?: string;
  /**
   * Maximum number of lines to show in preview
   * @default 3
   */
  maxLines?: number;
}

export const PopOutDescription = ({ description, title = "Description", maxLines = 3 }: PopOutDescriptionProps) => {
  const [dialogOpen, setDialogOpen] = useState(false);

  // Don't render if no description
  if (!description || description.trim().length === 0) {
    return null;
  }

  const handleOpenDialog = () => {
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  return (
    <>
      <Card
        sx={{
          "&:hover": {
            boxShadow: 4,
            transform: "translateY(-2px)",
          },
          display: "flex",
          flexDirection: "column",
          height: "100%",
          maxWidth: "100%",
          overflow: "hidden",
          position: "relative",
          transition: "all 0.2s",
        }}
      >
        <CardContent sx={{ flex: 1, pb: 2 }}>
          <Box sx={{ alignItems: "flex-start", display: "flex", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="h6" component="h2" fontWeight="bold">
              📝 {title}
            </Typography>
            <Button
              color="primary"
              onClick={handleOpenDialog}
              size="small"
              variant="text"
              data-testid="open-full-description-button"
              sx={{ minWidth: "auto", p: 0.5 }}
            >
              See More
            </Button>
          </Box>
          <GenericDescription
            description={description}
            sx={{
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: maxLines,
              display: "-webkit-box",
              overflow: "hidden",
            }}
          />
        </CardContent>
      </Card>

      {/* Full description dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        aria-labelledby="description-dialog-title"
      >
        <DialogContent>
          <CollapsibleDescription description={description} title={title} />
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button onClick={handleCloseDialog} color="primary">
              Close
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};
